import React from 'react';

import Typography from '@mui/material/Typography/Typography';
import QButton from 'components/QButton';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { splitItemsToArraySortedByPosition } from 'app-components/flux/transactions/splitsHelpers';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';
import SplitsView from 'components/TransactionSplits/SplitsView';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

const useStyles = makeStyles()((theme) => ({
  splitLine: {
    color: theme.palette.text.secondary,
    paddingTop: 6,
    display: 'flex',
    alignItems: 'center',
  },
  splitButton: {
    marginRight: 8,
  },
  splitIcon: {
    marginRight: 8,
    letterSpacing: 1,
  },
}));

type Props = {
  transaction: TransactionProps;
  onAssignSplits?: () => void;
  buttonLabel?: string;
  buttonSupportingText?: string;
  goToOtherSide?: (txn: TransactionProps) => void;
  disabledSplitsEdit?: boolean;
  /**
   * Use `series` when assigning splits to a recurring series.
   * Default is `default` for normal transaction splits.
   */
  variant?: 'default' | 'series';
};

const TransactionSplits = ({
  transaction,
  onAssignSplits,
  buttonLabel,
  buttonSupportingText,
  goToOtherSide,
  disabledSplitsEdit = false,
  variant = 'default',
}: Props): JSX.Element => {
  const { classes } = useStyles();

  const splitsFeatureFlag = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const isSeriesVariant = variant === 'series';
  const splitsEditDisabled = disabledSplitsEdit ?? !splitsFeatureFlag;
  if (isSeriesVariant && !splitsFeatureFlag) return <React.Fragment />;

  const txnAmount = transaction.amount;
  const splitItems = splitItemsToArraySortedByPosition(transaction.split?.items || []);
  const headerLabel = `${splitItems.length} Splits assigned`;
  const hasSplitsAssigned = splitItems.length > 0;

  const resolvedButtonLabel = buttonLabel ?? (isSeriesVariant ? 'Assign Splits' : 'Split');
  const resolvedButtonSupportingText =
    buttonSupportingText ??
    (isSeriesVariant
      ? "When transactions are matched to this Series, they'll be split in the same way."
      : 'Assign multiple tags or categories to this transaction.');

  return (
    <>
      {hasSplitsAssigned ? (
        <SplitsView
          txnId={transaction.id}
          splitItems={splitItems}
          amount={txnAmount}
          headerLabel={headerLabel}
          showRepeatIcon={Boolean(transaction.stModelId)}
          onEdit={splitsEditDisabled ? undefined : onAssignSplits}
          disabledEdit={splitsEditDisabled}
          goToOtherSide={goToOtherSide}
          variant={variant}
        />
      ) : (
        <Typography variant='captionRegular' className={classes.splitLine}>
          <QButton
            id='assign-splits-button'
            onClick={onAssignSplits}
            disableOutline
            className={classes.splitButton}
            disabled={splitsEditDisabled}
            startIcon={<CallSplitIcon className={classes.splitIcon} />}
          >
            {resolvedButtonLabel}
          </QButton>
          {resolvedButtonSupportingText}
        </Typography>
      )}
    </>
  );
};

export default TransactionSplits;
